import { Button, Collapse } from '@material-ui/core'
import { ChevronRight, LocalOffer } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { updateProject } from '../../../actions/projectActions'
import { useInput } from '../../../hooks/useInput'
import { useQuoteCalculator } from '../../../hooks/useQuoteCalculator'
import {
  PROMO_CODE_VALIDATE_RESET,
  validatePromoCode,
} from '../../../modules/promoCodeValidate'
import TextInput from '../../Form/TextInput'

const PromoCodeContainer = styled.div`
  width: 100%;

  .message.danger {
    margin-left: 32px;
  }
`

export const ToggleButton = styled(Button)`
  font-size: 14px !important;
  margin-left: -8px;

  &:hover {
    background-color: transparent;
  }

  .MuiButton-startIcon svg {
    width: 24px;
    height: 24px;
  }

  &.twist {
    .MuiButton-startIcon {
      transform: rotate(90deg);
    }
  }
`

interface Props {
  interactive: boolean
}

const PromoCode = ({ interactive }: Props) => {
  const dispatch = useDispatch()

  const {
    value: code,
    bind: bindCode,
    error: codeError,
    setError: setCodeError,
    setValue: setCode,
  } = useInput('')
  const [expanded, setExpanded] = useState(false)

  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { code: validCode, error } = useSelector(
    (state: RootStateOrAny) => state.validatePromoCode
  )
  const { loading, success } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )
  const { projectRequests } = useSelector(
    (state: RootStateOrAny) => state.projectRequestsDetails
  )

  const total = useQuoteCalculator(projectRequests)

  useEffect(() => {
    if (success) {
      setCode('')
    }
  }, [success, setCode])

  useEffect(() => {
    if (validCode) {
      const projectClone = {
        ...project,
        discountCodes: [...project.discountCodes, validCode],
      }

      setCodeError('')
      dispatch(updateProject(projectClone))
      dispatch({ type: PROMO_CODE_VALIDATE_RESET })
    }
  }, [validCode])

  const validateCode = () => {
    if (project.discountCodes.includes(code)) {
      setCodeError('Code already applied')
      return
    }

    if (!code) {
      setCodeError('Please enter a code')
      return
    } else {
      dispatch(validatePromoCode(code as string))
    }
  }

  const handleRemoveClick = (remove: PromoCode) => {
    const projectClone = {
      ...project,
      discountCodes: project.discountCodes.filter(
        (code: PromoCode) => code.name !== remove.name
      ),
    }

    setCodeError('')
    dispatch(updateProject(projectClone))
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  // First, we calculate the sum of all discount percentages in the discountCodes array.
  // In this case, it adds up all the discount percentages.
  const discountTotal = project.discountCodes.reduce(
    (acc: number, cur: PromoCode) => {
      return acc + cur.value
    },
    0
  )

  const calcDiscount = isNaN(Number(total))
    ? `-${discountTotal}%` // If `total` is NaN, we simply return the total discount as a percentage string.
    : // Otherwise, if `total` is a valid number, we proceed to calculate the discount amount.
      `-$${Number(
        // We multiply the total by the sum of the discount percentages divided by 100 to get the discount amount.
        (Number(total) * (3 / 100)).toFixed(0)
      ).toLocaleString()}`

  return (
    <>
      {!interactive ? (
        project.discountCodes.length > 0 ? (
          project.discountCodes.map((code: PromoCode, index: number) => (
            <div
              key={`code-${index}`}
              className="flex space-between m-t-8 primary"
            >
              <div className="fs14 bold">
                <div>
                  <LocalOffer style={{ fontSize: 16, marginRight: 1 }} />
                  {code.name}
                </div>
              </div>
              <div className="fs14 bold">
                -$
                {(Number(total) * (code.value / 100)).toLocaleString(
                  undefined,
                  { maximumFractionDigits: 0 }
                )}
              </div>
            </div>
          ))
        ) : null
      ) : (
        <>
          <div className={`flex align-center space-between `}>
            <div className="flex align-center">
              <ToggleButton
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="show more"
                className={`fs14 bold ${expanded ? 'twist' : ''}`}
                startIcon={<ChevronRight />}
                disableRipple
              >
                Promo code
              </ToggleButton>
            </div>
            {project.discountCodes.length > 0 ? (
              <div className="fs14">{calcDiscount}</div>
            ) : null}
          </div>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <div className="flex form-container  m-t--24">
              <PromoCodeContainer>
                <TextInput
                  name="discountCode"
                  label={''}
                  bindValue={bindCode}
                  error={codeError || error}
                  className="m-r-8 m-l-32 unset-width"
                  type="text"
                  disabled={false}
                  autoComplete={'false'}
                  width="calc(100% - 30px)"
                />
              </PromoCodeContainer>
              <Button
                style={{ height: 38, marginTop: 24 }}
                onClick={validateCode}
                variant={'outlined'}
                color="primary"
                disabled={loading}
                className={`m-l-8 ${loading ? 'loading' : ''}`}
              >
                Apply
              </Button>
            </div>

            {project.discountCodes.length > 0 &&
              project.discountCodes.map((code: PromoCode, index: number) => (
                <div
                  key={`code-${index}`}
                  className="flex space-between m-l-32 fs14 italic m-t-24 gray-text"
                >
                  <div>{code.name} applied</div>

                  <span
                    className="link"
                    onClick={() => handleRemoveClick(code)}
                  >
                    Remove
                  </span>
                </div>
              ))}
          </Collapse>
        </>
      )}
    </>
  )
}

export default PromoCode
